import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Home, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MesAppels from "@/components/maquette/MesAppels";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";

const PAGE = 20;

const Ligne = ({ p, statuts, onStatut }) => (
  <div className="at-fiche p-4 space-y-3" data-testid={`call-item-${p.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-bold truncate">{[p.civilite, p.prenom, p.nom].filter(Boolean).join(" ") || "Contact"}</p>
        <p className="text-sm at-muted truncate">{[p.ville, !p.ville?.includes(p.code_postal) && p.code_postal].filter(Boolean).join(" · ")}</p>
        <p className="text-xs at-muted mt-1 flex flex-wrap gap-x-2" data-testid={`call-meta-${p.id}`}>
          <span>{p.source}</span>{p.frais && <b style={{ color: "var(--temp-1)" }}>Frais</b>}
          <span>{p.tentatives} tentative(s)</span>{p.nb_biens > 1 && <span className="inline-flex items-center gap-1"><Home size={11} />{p.nb_biens} biens</span>}
        </p>
      </div>
      <span className="px-2.5 py-1 at-pill text-xs font-bold shrink-0" style={{ background: `color-mix(in srgb, ${p.statut_couleur} 16%, transparent)`, color: `color-mix(in srgb, ${p.statut_couleur} 62%, black)` }} data-testid={`call-status-${p.id}`}>{p.statut_nom}</span>
    </div>
    {p.prochain_appel_at && <p className="text-xs at-muted">Prochain appel : {formatDateHeure(p.prochain_appel_at)}</p>}
    <a href={p.telephone ? `tel:${p.telephone.replace(/\s/g, "")}` : undefined} className="at-btn at-btn-primary w-full" data-testid={`call-dial-${p.id}`}><Phone size={16} />{p.telephone || "Sans téléphone"}</a>
    <div className="grid grid-cols-2 gap-2">
      {statuts.map((s) => (
        <button key={s.code} type="button" onClick={() => onStatut(p, s)} className="min-h-[44px] px-2 at-pill text-xs font-bold leading-tight text-center"
          style={{ background: `color-mix(in srgb, ${s.couleur} 14%, transparent)`, color: `color-mix(in srgb, ${s.couleur} 62%, black)` }} data-testid={`call-set-${p.id}-${s.code}`}>{s.nom}</button>
      ))}
    </div>
  </div>
);

export default function MaListe() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  return user.role === "agent" && params.get("vue") !== "classique" ? <MesAppels /> : <MaListeClassique />;
}

function MaListeClassique() {
  const [data, setData] = useState(null);
  const [onglet, setOnglet] = useState("a_appeler");
  const [nb, setNb] = useState(PAGE);
  const [erreur, setErreur] = useState(null);
  const charger = useCallback(() => api.get("/appels/ma-liste").then((r) => { setData(r.data); setErreur(null); }).catch((e) => setErreur(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const statut = async (p, s) => {
    try { const { data: r } = await api.post(`/appels/${p.id}/statut`, { statut: s.code }); toast.success(r.fiche_id ? "Opportunité créée dans « Lead à traiter »." : r.texte); charger(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  if (!data) return <p className="at-muted" data-testid={erreur ? "calls-error" : "calls-loading"}>{erreur || "Chargement de votre liste…"}</p>;
  const liste = data[onglet];
  return (
    <div data-testid="page-ma-liste" className="min-w-0">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-1" data-testid="page-title">Ma liste d'appels</h1>
      <p className="at-muted mb-4" data-testid="calls-summary">{data.a_appeler.length} à appeler · {data.plus_tard.length} plus tard · {data.faits_aujourdhui} appel(s) aujourd'hui</p>
      <Link to="/focus" className="at-btn at-btn-primary w-full mb-4" data-testid="calls-next-contact"><Zap size={16} />Prochaine fiche (mode focus)</Link>
      <div className="flex gap-2 mb-4">
        {[["a_appeler", "À appeler"], ["plus_tard", "Plus tard"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setOnglet(k); setNb(PAGE); }} className="min-h-[44px] px-4 at-pill font-semibold" data-testid={`calls-tab-${k}`}
            style={{ background: onglet === k ? "var(--accent)" : "var(--surface)", color: onglet === k ? "var(--accent-ink)" : "var(--ink)" }}>{l} ({data[k].length})</button>
        ))}
      </div>
      {liste.length === 0 ? <Vide texte="Aucun contact pour le moment." testId="calls-empty" /> : (
        <>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="calls-list">{liste.slice(0, nb).map((p) => <Ligne key={p.id} p={p} statuts={data.statuts} onStatut={statut} />)}</div>
          {liste.length > nb && <button type="button" onClick={() => setNb(nb + PAGE)} className="at-btn w-full mt-4 min-h-[44px]" data-testid="calls-show-more">Afficher plus ({liste.length - nb} restant(s))</button>}
        </>
      )}
    </div>
  );
}
